import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { snakeCase, startCase, uniq } from 'lodash/fp';
import { useEffect } from 'react';
import { Controller, FieldErrors } from 'react-hook-form';
import styled, { css } from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import {
  EXTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES,
  EXTRACTOR_TYPE_TO_DESCRIPTION,
  EXTRACTOR_TYPE_TO_DISPLAY_NAME,
  INTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES,
} from 'dashboard/components/ExtractionPanel/constants';
import ExtractionFieldWritebacksPanel, {
  fieldWriteBackRequestsToFormState,
  formStateToFieldWriteBackAndDocumentFieldRequests,
  validateFieldWriteBacksPanelForm,
} from 'dashboard/components/ExtractionPanel/ExtractionFieldWriteBacksPanel';
import GuidedExtractionV2Panel, {
  guidedExtractionV2RequestToFormState,
  guidedFormStateToExtractionV2Request,
  validateGuidedExtractionV2PanelForm,
} from 'dashboard/components/ExtractionPanel/GuidedExtractionV2Panel';
import GuidedExtractionV3Panel, {
  guidedExtractionV3RequestToFormState,
  guidedFormStateToExtractionV3Request,
  validateGuidedExtractionV3PanelForm,
} from 'dashboard/components/ExtractionPanel/GuidedExtractionV3Panel';
import TransactionGuidedExtractionPanel, {
  extractionToTransactionGuidedExtractionFormState,
  transactionGuidedExtractionFormStateToExtraction,
  validateTransactionGuidedExtractionFormState,
} from 'dashboard/components/ExtractionPanel/TransactionGuidedExtractionPanel';
import UnguidedExtractionPanel, {
  formStateToUnguidedExtractionRequest,
  unguidedExtractionRequestToExtractionFormState,
  validateUnguidedExtractionPanelForm,
} from 'dashboard/components/ExtractionPanel/UnguidedExtractionPanel';
import {
  CONFIGURABLE_FIELD_SCHEMA_TYPES,
  ExtractionPanelForm,
  extractionUsesAi,
  MatchRequirement,
} from 'dashboard/components/ExtractionPanel/util';
import { DOCUMENT_AI_BLOCK_MESSAGE } from 'dashboard/forms/FormVerificationDocumentTemplateConfig/FormVerificationDocumentTemplateConfig';
import {
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  FieldWriteBackRequest,
  MatchLevel,
} from 'dashboard/models/document';
import { DocumentExtractionCheckConfig } from 'dashboard/models/verification-check-config/document-extracted-properties-detection';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import { ErrorText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FieldSchema } from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { CheckName } from 'lib/verificationCheck';

export const DEFAULT_AI_MODEL = 'gpt-4o';

const PANEL_FOOTER_HEIGHT = 64;

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const Section = styled.section`
  padding: ${Spacing[4]};
`;

const SectionBreak = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const SelectOptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledSticker = styled(Sticker)`
  flex-shrink: 0;
`;

// TODO (Documents): Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) =>
    !disabled &&
    css`
      text-decoration: underline;
      text-decoration-style: dotted;
    `}
`;

const extractionRequestToFormState = (
  extractionRequest: ExtractionRequest,
  fieldWriteBacks: FieldWriteBackRequest[],
  fieldSchema: FieldSchema[],
  hasInquiryTemplate: boolean,
): Partial<ExtractionPanelForm> => {
  let base: Partial<ExtractionPanelForm> = {
    fieldName: startCase(extractionRequest['field-name']),
    extractionType: extractionRequest['extraction-type'],
  };

  base = {
    ...base,
    ...fieldWriteBackRequestsToFormState(extractionRequest, fieldWriteBacks, fieldSchema),
  };

  switch (extractionRequest['extraction-type']) {
    case ExtractionType.Guided: {
      if (hasInquiryTemplate) {
        if (extractionRequest.version === '2.0') {
          return { ...base, ...guidedExtractionV2RequestToFormState(extractionRequest) };
        } else {
          return { ...base, ...guidedExtractionV3RequestToFormState(extractionRequest) };
        }
      } else {
        return { ...base, ...extractionToTransactionGuidedExtractionFormState(extractionRequest) };
      }
    }
    case ExtractionType.Unguided: {
      return {
        ...base,
        ...unguidedExtractionRequestToExtractionFormState(extractionRequest),
      };
    }
  }
};

const formStateToRequests = (
  data: ExtractionPanelForm,
  oldValues: Partial<ExtractionPanelForm>,
  oldExtractionRequest: ExtractionRequest | null,
  fieldWriteBacks: FieldWriteBackRequest[],
  documentFields: FieldSchema[],
): {
  extractionRequest: ExtractionRequest;
  fieldWriteBackRequests: FieldWriteBackRequest[];
  documentFieldRequests: FieldSchema[] | null;
} => {
  let extractionRequest: ExtractionRequest;

  switch (data.extractorType) {
    case ExtractorType.InquiryFieldComparison:
      if (data.version === '2.0') {
        extractionRequest = guidedFormStateToExtractionV2Request(data);
      } else {
        extractionRequest = guidedFormStateToExtractionV3Request(data);
      }
      break;
    case ExtractorType.TransactionFieldComparison:
      extractionRequest = transactionGuidedExtractionFormStateToExtraction(data);
      break;
    default:
      extractionRequest = formStateToUnguidedExtractionRequest(data, oldExtractionRequest);
      break;
  }

  const { fieldWriteBackRequests, documentFieldRequests } =
    formStateToFieldWriteBackAndDocumentFieldRequests(
      data,
      oldValues,
      oldExtractionRequest,
      fieldWriteBacks,
      documentFields,
    );

  return { extractionRequest, fieldWriteBackRequests, documentFieldRequests };
};

const errorRequired = { type: 'required', message: 'Required' };
const fieldNameRegex = /^[_\s]*[a-zA-Z0-9][_a-zA-Z0-9\s]*$/;

const validateFormData = (
  data: ExtractionPanelForm,
  vtmplVersion: VerificationTemplateVersionDocument,
  oldExtraction: ExtractionRequest | null,
  inquiryFields: FieldSchema[],
) => {
  let errors: FieldErrors<ExtractionPanelForm> = {};

  const { extractions, 'field-schemas': documentFields = [] } = vtmplVersion.attributes;

  const usedExtractionNames = extractions
    .map((e) => snakeCase(e['field-name']))
    .filter((e) => e !== oldExtraction?.['field-name']);
  const unconfigurableInquiryFieldNames = (inquiryFields || [])
    .filter((field) => !CONFIGURABLE_FIELD_SCHEMA_TYPES.includes(field.type))
    .map((field) => field.key);

  if (data?.fieldName == null || data?.fieldName === '') {
    errors.fieldName = errorRequired;
  } else if (!data?.fieldName.match(fieldNameRegex)) {
    errors.fieldName = {
      type: 'pattern',
      message: 'Extraction label may only contain letters, number, underscores, and spaces.',
    };
  } else if (usedExtractionNames.includes(snakeCase(data.fieldName))) {
    errors.fieldName = {
      type: 'conflict',
      message: 'An extraction with this label already exists. Please choose a different label.',
    };
  }
  switch (data?.extractorType) {
    case ExtractorType.InquiryFieldComparison:
      if (data.version === '2.0') {
        errors = {
          ...errors,
          ...validateGuidedExtractionV2PanelForm(data, unconfigurableInquiryFieldNames),
        };
      } else {
        errors = {
          ...errors,
          ...validateGuidedExtractionV3PanelForm(data, unconfigurableInquiryFieldNames),
        };
      }
      break;
    case ExtractorType.TransactionFieldComparison:
      errors = { ...errors, ...validateTransactionGuidedExtractionFormState(data) };
      break;
    default:
      errors = {
        ...errors,
        ...validateUnguidedExtractionPanelForm(data, usedExtractionNames),
      };
      break;
  }

  errors = {
    ...errors,
    ...validateFieldWriteBacksPanelForm(data, documentFields),
  };

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface ExtractionPanelProps {
  vtmplVersion: VerificationTemplateVersionDocument;
  // index of currently selected extraction (null if new extraction)
  extractionIndex: number | null;
  fieldSchema: FieldSchema[];
  closeExtraction: () => void;
  addExtraction: (
    extractionRequest: ExtractionRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema[] | null,
    isRequired: boolean,
  ) => void;
  updateExtraction: (
    extractionRequest: ExtractionRequest,
    fieldWriteBackRequests: FieldWriteBackRequest[],
    documentFieldRequest: FieldSchema[] | null,
    isRequired: boolean,
  ) => void;
  deleteExtraction: () => void;
  aiLimitReached: boolean;
  hasInquiryTemplate: boolean;
}

const ExtractionPanel = ({
  vtmplVersion,
  extractionIndex,
  fieldSchema,
  closeExtraction,
  addExtraction,
  updateExtraction,
  deleteExtraction,
  aiLimitReached,
  hasInquiryTemplate,
}: ExtractionPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeExtraction();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [closeExtraction]);

  const isITEInternal = useIsITEInternal();

  const { extractions } = vtmplVersion.attributes;

  const extraction = extractionIndex != null ? extractions[extractionIndex] : null;
  const isNew = extraction == null;

  let defaultValues: Partial<ExtractionPanelForm> = {
    extractionType: ExtractionType.Unguided,
    extractorType: undefined,
    fieldType: ExtractionFieldType.Ai,
    matchLevelMinimum: MatchLevel.Partial,
    isRequired: false,
    saveToDocumentField: false,
    fieldWriteBackForms: [],
  };

  const selectedExtraction = extractionIndex != null ? extractions[extractionIndex] : null;
  const possibleInquiryFieldNames = fieldSchema
    .filter((field) => CONFIGURABLE_FIELD_SCHEMA_TYPES.includes(field.type))
    .map((field) => field.key)
    .sort();

  const documentFields = vtmplVersion.attributes['field-schemas'] || [];
  const fieldWriteBacks = vtmplVersion.attributes['field-write-backs'] || [];

  if (!isNew) {
    const verificationChecks = vtmplVersion.attributes['verification-checks'];
    const extractedPropertiesDetection = verificationChecks.find(
      (v) => v.check === CheckName.DocumentExtractedPropertiesDetection,
    ) as DocumentExtractionCheckConfig | undefined;
    const requiredExtractions = extractedPropertiesDetection?.['required-extractions'] || [];
    const requiredExtractionsFieldNames = requiredExtractions.map(
      (requiredExtraction) => requiredExtraction['field-name'],
    );
    const isRequired = requiredExtractionsFieldNames.includes(extraction['field-name']);

    defaultValues = {
      ...defaultValues,
      ...extractionRequestToFormState(extraction, fieldWriteBacks, fieldSchema, hasInquiryTemplate),
      isRequired: isRequired,
    };
  }

  const formReturn = useFormSecurely<ExtractionPanelForm>({
    defaultValues,
    resolver: (data) => validateFormData(data, vtmplVersion, selectedExtraction, fieldSchema),
    mode: 'onChange',
  });
  const { register, formState, watch, control, handleSubmit } = formReturn;
  const { errors } = formState;
  const extractionType = watch('extractionType');
  const extractorType = watch('extractorType');
  const matchRequirement = watch('matchRequirement');

  const handleSubmitForm = handleSubmit((data) => {
    const { extractionRequest, fieldWriteBackRequests, documentFieldRequests } =
      formStateToRequests(data, defaultValues, extraction, fieldWriteBacks, documentFields);
    if (isNew) {
      addExtraction(
        extractionRequest,
        fieldWriteBackRequests,
        documentFieldRequests,
        data.isRequired,
      );
    } else {
      updateExtraction(
        extractionRequest,
        fieldWriteBackRequests,
        documentFieldRequests,
        data.isRequired,
      );
    }
    closeExtraction();
  });

  const handleCancel = () => {
    closeExtraction();
  };

  const handleDelete = () => {
    deleteExtraction();
    closeExtraction();
  };

  const readOnly =
    (extractionType === ExtractionType.Guided && selectedExtraction?.version === '2.0') ||
    (extractorType &&
      (!INTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES.includes(extractorType) ||
        (!isITEInternal && !EXTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES.includes(extractorType))));

  let panel = undefined;
  switch (extractorType) {
    case ExtractorType.InquiryFieldComparison:
      panel = (
        <GuidedExtractionV3Panel
          formReturn={formReturn}
          possibleInquiryFieldNames={possibleInquiryFieldNames}
          disabled={readOnly}
        />
      );
      break;
    case ExtractorType.TransactionFieldComparison:
      panel = <TransactionGuidedExtractionPanel formReturn={formReturn} disabled={readOnly} />;
      break;
    default:
      panel = (
        <>
          <Section style={{ paddingTop: 0 }}>
            <UnguidedExtractionPanel formReturn={formReturn} disabled={readOnly} />
          </Section>
          <SectionBreak />
        </>
      );
      break;
  }

  const guidedV2 =
    extractionType === ExtractionType.Guided && selectedExtraction?.version === '2.0';
  if (guidedV2) {
    panel = <GuidedExtractionV2Panel formReturn={formReturn} disabled={readOnly} />;
  }

  const wasAi = !isNew && extractionUsesAi(extraction);
  const isAi =
    extractorType === ExtractorType.Ai ||
    (extractorType === ExtractorType.InquiryFieldComparison &&
      matchRequirement === MatchRequirement.LooseAi) ||
    (extractorType === ExtractorType.TransactionFieldComparison &&
      matchRequirement === MatchRequirement.LooseAi);

  return (
    <Form id='extraction-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            {readOnly ? 'View' : isNew ? 'Add' : 'Edit'} extraction
          </Paragraph>
        </TitleContainer>
        <Section>
          <Tooltip
            side='right'
            trigger={
              <StyledLabel>
                <TooltipIndicatorWrapper disabled={readOnly}>
                  Extraction label
                </TooltipIndicatorWrapper>
              </StyledLabel>
            }
            disabled={readOnly}
          >
            <Paragraph>
              Specify a descriptive extraction label. This label is used to assist AI extractions.
            </Paragraph>
          </Tooltip>
          <FieldWrapper>
            <Input
              {...register('fieldName')}
              placeholder='Enter extraction label'
              hasError={!!errors?.fieldName}
              autoFocus
              disabled={readOnly}
            />
          </FieldWrapper>
          <FieldErrorText message={errors?.fieldName?.message} />
          <Spacer height={1.5} />
          <Controller
            name='isRequired'
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                label={
                  <Tooltip
                    side='right'
                    trigger={
                      <TooltipIndicatorWrapper disabled={readOnly}>
                        Require extraction
                      </TooltipIndicatorWrapper>
                    }
                    disabled={readOnly}
                  >
                    <Paragraph>
                      When checked, the verification will fail if this extraction cannot be found in
                      the document.
                    </Paragraph>
                  </Tooltip>
                }
                checked={field.value}
                disabled={readOnly}
              />
            )}
          />
        </Section>
        <SectionBreak />
        <Section>
          <Paragraph weight='bold' color='primary'>
            Extraction method
          </Paragraph>
          {!guidedV2 && (
            <>
              <Spacer height={1.5} />
              <StyledLabel>Extractor type</StyledLabel>
              <Controller
                name='extractorType'
                control={control}
                render={({ field }) => (
                  <FieldWrapper>
                    <Select
                      {...field}
                      placeholder='Select extractor type'
                      hasError={!!errors?.extractorType}
                      valueContainerWidth='100%'
                      disabled={readOnly}
                    >
                      {uniq([
                        ...EXTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES,
                        ...((isITEInternal && INTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES) || []),
                        extractorType,
                      ])
                        .filter(
                          (et) =>
                            (et !== ExtractorType.InquiryFieldComparison || hasInquiryTemplate) &&
                            (et !== ExtractorType.TransactionFieldComparison ||
                              !hasInquiryTemplate),
                        )
                        .map(
                          (extractorType) =>
                            extractorType && (
                              <Option
                                key={extractorType}
                                value={extractorType}
                                subtitle={EXTRACTOR_TYPE_TO_DESCRIPTION[extractorType]}
                                stretchLabel={true}
                              >
                                <SelectOptionContainer>
                                  {EXTRACTOR_TYPE_TO_DISPLAY_NAME[extractorType]}
                                  {isITEInternal &&
                                    (EXTERNALLY_CONFIGURABLE_EXTRACTOR_TYPES.includes(
                                      extractorType,
                                    ) ? (
                                      <StyledSticker variant='primary'>Recommended</StyledSticker>
                                    ) : (
                                      <StyledSticker variant='basic'>Internal Only</StyledSticker>
                                    ))}
                                </SelectOptionContainer>
                              </Option>
                            ),
                        )}
                    </Select>
                  </FieldWrapper>
                )}
              />
              <FieldErrorText message={errors?.extractorType?.message} />
            </>
          )}
        </Section>
        {panel}
        <Section>
          <Paragraph weight='bold' color='primary'>
            Advanced
          </Paragraph>
          <Spacer height={1.5} />
          <ExtractionFieldWritebacksPanel
            formReturn={formReturn}
            vtmplVersion={vtmplVersion}
            disabled={readOnly}
          />
        </Section>
      </BodyContainer>
      <FooterContainer>
        <HStack style={{ flexGrow: 1 }}>
          {!readOnly && (
            <Tooltip
              side='top'
              disabled={!aiLimitReached || !isAi || wasAi}
              trigger={
                <Button
                  id={`extraction-panel__${isNew ? 'add' : 'update'}-button`}
                  type='submit'
                  width='auto'
                  onClick={handleSubmitForm}
                  disabled={aiLimitReached && isAi && !wasAi}
                >
                  {isNew ? 'Add' : 'Update'}
                </Button>
              }
            >
              {aiLimitReached && <Paragraph>{DOCUMENT_AI_BLOCK_MESSAGE}</Paragraph>}
            </Tooltip>
          )}
          <Button
            id='extraction-panel__close-button'
            width='auto'
            variant='basic'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </HStack>
        <IconButton id='extraction-panel__delete-button' onClick={handleDelete} disabled={isNew}>
          <TrashIcon />
        </IconButton>
      </FooterContainer>
    </Form>
  );
};

export default ExtractionPanel;
